import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  User,
  CalendarCheck,
  FileText,
  Calendar,
  Bell,
  Award,
  LogOut,
  Shield,
  Menu,
  X,
  Sun,
  Moon,
} from "lucide-react";

export const StudentLayout = () => {
  const { currentUser, userProfile, logout, theme, toggleTheme } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { label: "Dashboard", path: "/student/dashboard", icon: <LayoutDashboard className="w-4 h-4" /> },
    { label: "Profile", path: "/student/profile", icon: <User className="w-4 h-4" /> },
    { label: "Attendance", path: "/student/attendance", icon: <CalendarCheck className="w-4 h-4" /> },
    { label: "Study Material", path: "/student/materials", icon: <FileText className="w-4 h-4" /> },
    { label: "Schedule", path: "/student/schedule", icon: <Calendar className="w-4 h-4" /> },
    { label: "Announcements", path: "/student/announcements", icon: <Bell className="w-4 h-4" /> },
    { label: "Certificate", path: "/student/certificate", icon: <Award className="w-4 h-4" /> },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col md:flex-row">
      {/* Mobile Topbar */}
      <div className="md:hidden flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center space-x-2">
          <Shield className="w-5 h-5 text-emerald-400" />
          <span className="font-bold text-sm tracking-tight">Kian Cyber Student Portal</span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded-lg border border-border text-foreground"
          >
            {theme === "dark" ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-1.5 rounded-lg border border-border text-foreground"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Sidebar Navigation */}
      <aside
        className={`${
          mobileOpen ? "block" : "hidden"
        } md:block w-full md:w-64 border-r border-border bg-card/95 md:min-h-screen flex flex-col justify-between p-4 z-30`}
      >
        <div className="space-y-6">
          {/* Brand header */}
          <div className="hidden md:flex items-center space-x-3 px-2 py-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <Shield className="w-4 h-4" />
            </div>
            <div>
              <div className="font-bold text-sm tracking-tight">Kian Technologies</div>
              <div className="text-[10px] text-muted-foreground font-mono">Student Portal</div>
            </div>
          </div>

          {/* Student Profile Quick Badge */}
          <div className="p-3 rounded-xl bg-muted/50 border border-border flex items-center space-x-3">
            {userProfile?.photoUrl ? (
              <img
                src={userProfile.photoUrl}
                alt="Student"
                className="w-10 h-10 rounded-lg object-cover border border-border"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-mono text-xs font-bold">
                {userProfile?.fullName ? userProfile.fullName.charAt(0).toUpperCase() : "S"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate text-foreground">
                {userProfile?.fullName || currentUser?.email?.split("@")[0] || "Enrolled Student"}
              </div>
              <div className="text-[10px] font-mono text-emerald-400 truncate">
                {userProfile?.studentId || "Student Account"}
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="space-y-1">
            {navItems.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`student-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg text-xs font-mono font-medium transition-all ${
                    active
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shadow-sm"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground border border-transparent"
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="pt-6 border-t border-border space-y-2">
          <div className="hidden md:flex items-center justify-between px-3 py-1 text-xs text-muted-foreground font-mono">
            <span>Theme</span>
            <button
              onClick={toggleTheme}
              className="p-1 rounded border border-border hover:bg-muted text-foreground"
            >
              {theme === "dark" ? <Sun className="w-3.5 h-3.5 text-amber-400" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
          </div>
          <button
            onClick={handleLogout}
            data-testid="student-logout-btn"
            className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-xs font-mono font-medium text-destructive hover:bg-destructive/10 border border-transparent hover:border-destructive/20 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Viewport */}
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
};
